import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useState, useEffect, useMemo } from 'react';
import { Program, Enrollment, getEnrollmentPaymentStatus, Invoice, KALEIDO_PROGRAM_TYPES } from '../types/program';
import { Link, useNavigate } from 'react-router-dom';
import { AddChildForm } from '../components/AddChildForm';
import { ProgramCard } from '../components/ProgramCard';
import { AlertCircle, CalendarClock, History, Repeat, Sparkles, Wallet as WalletIcon } from 'lucide-react';
import { TodaySection, type TodayStudent } from '../components/dashboard/TodaySection';
import { NeedsYouSection, type DueAction, type DueInvoice } from '../components/dashboard/NeedsYouSection';
import { WeekCalendar } from '../components/dashboard/WeekCalendar';
import { QuickLinksSection, type QuickLink } from '../components/dashboard/QuickLinksSection';
import type { WeekEnrollment } from '../utils/weekSchedule';
import { TYPE_SECTION_WIDE } from '../components/typeScale';
import settings from '../config/settings.json';
import { trackEvent } from '../utils/analytics';
import { formatTimeOfDay, formatWeekdayDateDisplay, getTodayDateStringInBusinessTZ } from '../utils/date';
import { hasSlotStarted, isSlotLocked } from '../utils/signupLock';
import { useNow } from '../hooks/useNow';
import { addMonths, getFlexConfig, nextEditableMonth } from '../utils/flexPlan';
import { isSignupEnabledByEnv } from '../utils/env';
import {
  eligibleStudents,
  isRoundOpen,
  type EligibilityCandidate,
  type SignupRound,
} from '../utils/signupEligibility';
import { useBrandProfile } from '../contexts/brand-profile';
import { brandProfileOf } from '../config/brandProfiles';
import { CampPaymentNudge } from '../components/CampPaymentNudge';
import { useUnpaidInvoiceNudge } from '../hooks/useUnpaidInvoiceNudge';
import { useLabelParts, useLabels } from '../config/useLabels';
import { PROGRAM_LOCATION_EMBED } from '../types/location';
import { useCartHeldInvoiceIds } from '../hooks/useCartHeldInvoiceIds';

export interface StudentMetadata {
  school?: string;
  grade?: string;
  interested_programs?: string[];
}

export interface Student {
  id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string | null;
  active: boolean;
  metadata?: StudentMetadata;
}

export interface EnrollmentWithDetails extends Enrollment {
  students: Student;
  programs: Program;
  invoices: Invoice[];
}

/** One child's slot in an open signup round, with when it is. */
interface SignupBooking {
  eventId: string;
  studentId: string;
  /** `DATE`, business-local. */
  slotDate: string;
  /** `TIME`, business-local. */
  startTime: string;
}

export interface StudentEnrollments {
  student: Student;
  enrollments: EnrollmentWithDetails[];
}

/**
 * The one invoice an enrollment row is about, and whether it is the parent's move.
 *
 * Both the "Needs you" block and the enrollment row's own status chip read this, and they
 * have to agree: a row showing "Paid" beside an invoice listed as outstanding would be the
 * portal contradicting itself on the same screen.
 *
 * `isPayable` deliberately excludes `payment_processing`. An ACH payment sits in flight for
 * one to three days, and offering to pay again during that window is how a family ends up
 * having paid twice.
 */
function getEnrollmentBilling(enrollment: EnrollmentWithDetails, heldByCart: ReadonlySet<string>): {
  invoice: Invoice | undefined;
  amountDue: number;
  paymentStatus: string;
  isPayable: boolean;
} {
  const unpaid = (enrollment.invoices ?? []).filter(inv =>
    ['pending', 'partial', 'overdue'].includes(inv.status)
  );
  const invoice = unpaid.length > 0
    ? unpaid.reduce((latest, inv) => (new Date(inv.due_date) > new Date(latest.due_date) ? inv : latest))
    : enrollment.invoices?.[0];
  const amountDue = invoice ? invoice.total_amount - invoice.amount_paid : 0;
  const paymentStatus = getEnrollmentPaymentStatus(enrollment, enrollment.invoices, heldByCart);
  const isPayable =
    amountDue > 0 &&
    !['paid', 'payment_processing', 'voided', 'no_invoice'].includes(paymentStatus.toLowerCase());
  return { invoice, amountDue, paymentStatus, isPayable };
}

// Add PricingItem and ProgramWithPricing types for local use
export interface LineItemConfig {
  id: string;
  name: string;
  type?: string;
  description?: string;
  subtype?: string;
}
export interface PricingItem {
  amount: number;
  is_optional: boolean;
  line_item_configs: LineItemConfig | LineItemConfig[];
}
export interface ProgramWithPricing extends Program {
  pricing?: PricingItem[];
  tags?: string[] | null;
}

export interface RecommendationResult {
  program: ProgramWithPricing;
  score: number;
  reason: string | null;
  reasonType: 'enrolled' | 'interest' | 'grade' | 'school' | 'similar' | 'new' | 'starting-soon' | 'popular' | null;
}

// Interest category → matching program types
const INTEREST_MAP: Record<string, string[]> = {
  extracurricular: ['extracurricular', 'artistic', 'language', 'logic-strategy', 'sports'],
  'after-school': ['after-school', 'academic'],
  camps: ['camp'],
  // Kaleido's interest checkboxes store its `programs.type` values verbatim (see
  // BRAND_PROFILES.kaleido.intake), so each maps to itself. Derived rather than written out,
  // so adding a Kaleido category cannot leave its interest silently unscored. An interest
  // with no entry here is not an error — the lookup below falls back to no boost.
  ...Object.fromEntries(KALEIDO_PROGRAM_TYPES.map(meta => [meta.value, [meta.value]])),
};

/** How many recommendations the dashboard will show. */
const MAX_RECOMMENDATIONS = 5;

// Priority order for reason labels (lower index = higher priority)
const REASON_PRIORITY: NonNullable<RecommendationResult['reasonType']>[] = [
  'enrolled', 'interest', 'grade', 'school', 'similar', 'new', 'starting-soon', 'popular',
];

function scoreAndReasonProgram(
  program: ProgramWithPricing,
  enrollmentCountMap: Map<string, number>,
  maxEnrollmentCount: number,
  enrolledTypeToProgramName: Map<string, string>,
  interestCategories: Set<string>,
  studentGrades: Set<string>,
  studentSchools: Set<string>,
  similarCountMap: Map<string, number>,
): RecommendationResult {
  let score = 0;
  const triggered: Array<{ type: NonNullable<RecommendationResult['reasonType']>; label: string }> = [];

  const addReason = (type: NonNullable<RecommendationResult['reasonType']>, label: string) => {
    triggered.push({ type, label });
  };

  // Camp boost (no reason label)
  if (program.type === 'camp') score += 30;

  // Past enrollment type match
  const enrolledProgramName = enrolledTypeToProgramName.get(program.type);
  if (enrolledProgramName !== undefined) {
    score += 25;
    addReason('enrolled', `Your kid enjoyed ${enrolledProgramName}`);
  }

  // Interest match
  for (const category of interestCategories) {
    const types = INTEREST_MAP[category] ?? [];
    if (types.includes(program.type)) {
      score += 20;
      addReason('interest', 'Based on your interests');
      break;
    }
  }

  // New program (<30d → +20, 30-60d → +10)
  const daysSinceCreated = (Date.now() - new Date(program.created_at).getTime()) / 86_400_000;
  if (daysSinceCreated < 30) {
    score += 20;
    addReason('new', 'New this season');
  } else if (daysSinceCreated < 60) {
    score += 10;
    addReason('new', 'New this season');
  }

  // Grade tag match
  const programTags = (program.tags ?? []).map(t => t.toLowerCase());
  for (const grade of studentGrades) {
    if (programTags.includes(`grade ${grade.toLowerCase()}`)) {
      score += 15;
      addReason('grade', `Great for grade ${grade} students`);
      break;
    }
  }

  // School tag match
  for (const school of studentSchools) {
    if (programTags.includes(school.toLowerCase())) {
      score += 15;
      addReason('school', `Popular at ${school}`);
      break;
    }
  }

  // Similar kids (>=3 students at same school+grade enrolled)
  const similarCount = similarCountMap.get(program.id) ?? 0;
  if (similarCount >= 3) {
    const schoolName = studentSchools.size > 0 ? [...studentSchools][0] : 'your school';
    score += 15;
    addReason('similar', `Popular with ${schoolName} students`);
  }

  // Starting soon (<=30d → +15, 31-90d → +5 no label)
  const daysUntilStart = (new Date(program.start_date).getTime() - Date.now()) / 86_400_000;
  if (daysUntilStart >= 0 && daysUntilStart <= 30) {
    score += 15;
    addReason('starting-soon', 'Starting soon');
  } else if (daysUntilStart > 30 && daysUntilStart <= 90) {
    score += 5;
  }

  // Low enrollment (0–10 pts, no label)
  const count = enrollmentCountMap.get(program.id) ?? 0;
  if (maxEnrollmentCount > 0) {
    score += Math.round(10 * (1 - count / maxEnrollmentCount));
  } else {
    score += 10;
  }

  // High enrollment — top 25%
  if (maxEnrollmentCount > 0 && count >= 0.75 * maxEnrollmentCount) {
    score += 5;
    addReason('popular', 'Popular');
  }

  // Pick the highest-priority triggered reason
  triggered.sort((a, b) => REASON_PRIORITY.indexOf(a.type) - REASON_PRIORITY.indexOf(b.type));
  const top = triggered[0];

  return { program, score, reason: top?.label ?? null, reasonType: top?.type ?? null };
}

/** "Harper", "Harper and Woody", "Harper, Woody and Ana" — read aloud, not comma-spliced. */
function formatNameList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

export default function Dashboard() {
  // Invoices a bank bundle payment is still debiting: no payments row yet, but money is on its way.
  const cartHeldInvoiceIds = useCartHeldInvoiceIds();
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const t = useLabels();
  const label = useLabelParts();
  const showSignupEnv = isSignupEnabledByEnv();

  const now = useNow();

  // The open round, if there is one. Its title and id are both needed: the title names the
  // thing in "Needs you", and the id links straight to it rather than to the event picker.
  const { data: activeSignupEvents = [] } = useQuery<(SignupRound & { lockHoursBefore?: number | null })[]>({
    queryKey: ['active-signup-events'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('signup_events')
        .select('id, title, end_date, target_grades, lock_hours_before')
        .eq('is_active', true)
        .order('created_at', { ascending: false });
      if (error) throw error;
      const rows = (data ?? []) as {
        id: string;
        title: string;
        end_date: string | null;
        target_grades: string[] | null;
        lock_hours_before: number | null;
      }[];
      return rows.map(row => ({
        id: row.id,
        title: row.title,
        endDate: row.end_date,
        targetGrades: row.target_grades,
        lockHoursBefore: row.lock_hours_before,
      }));
    },
    enabled: showSignupEnv,
  });
  const [error, setError] = useState<string | null>(null);
  const [showAddChild, setShowAddChild] = useState(false);
  const [showContactAdmin, setShowContactAdmin] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeReasonId, setActiveReasonId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { activeBrand, isMultiBrand } = useBrandProfile();
  // Check for modern mobile nav
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || /Mobi|Android/i.test(navigator.userAgent));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);


  // Shared parent ID — cached for 15 min, reused by students + enrollment queries
  // Same query key + shape as Payments.tsx so they share the React Query cache
  const { data: parentData, isLoading: parentIdLoading } = useQuery<{ id: string } | null>({
    queryKey: ['current-parent', user?.email],
    queryFn: async () => {
      if (!user?.email) return null;
      const { data, error } = await supabase
        .from('parents')
        .select('id')
        .eq('email', user.email)
        .single();
      if (error) return null;
      return data ?? null;
    },
    enabled: !!user?.email,
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
  const parentId = parentData?.id ?? null;

  // Fetch students — single join query, fires in parallel with enrollment query
  const { data: students, isLoading: studentsLoading } = useQuery<Student[]>({
    queryKey: ['dashboard-students', parentId],
    queryFn: async () => {
      if (!parentId) return [];
      const { data, error } = await supabase
        .from('student_parents')
        .select('students!inner(id, first_name, last_name, date_of_birth, active, metadata)')
        .eq('parent_id', parentId);
      if (error) return [];
      return (data ?? []).flatMap((row: { students: Student | Student[] }) =>
        Array.isArray(row.students) ? row.students : [row.students]
      ).filter(Boolean) as Student[];
    },
    enabled: !!parentId,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  // Who has already booked. Without this the row would keep saying a family still needs to
  // sign up after they have — which is the one thing "Needs you" must never do, or parents
  // stop believing the whole block. One slot per student per event, same rule the signup page
  // and the database's unique index both enforce.
  // `?? []` and not just the destructuring default: a query that resolves to null keeps null,
  // and this runs before the loading guard below.
  const activeSignupEventIds = useMemo(
    () => (activeSignupEvents ?? []).map(round => round.id).sort(),
    [activeSignupEvents]
  );

  const signupStudentIds = useMemo(() => (students ?? []).map(s => s.id).sort(), [students]);

  // Each booking carries its round, so with two rounds open a slot in one is never read as a
  // slot in the other — and its time, so a booked family is told when, not just that.
  const { data: signupBookings } = useQuery<SignupBooking[]>({
    queryKey: ['signup-bookings', activeSignupEventIds.join(','), signupStudentIds.join(',')],
    queryFn: async () => {
      if (activeSignupEventIds.length === 0 || signupStudentIds.length === 0) return [];
      const { data, error } = await supabase
        .from('signup_registrations')
        .select('student_id, signup_slots!inner(event_id, slot_date, start_time)')
        .in('signup_slots.event_id', activeSignupEventIds)
        .in('student_id', signupStudentIds);
      if (error) throw error;
      type SlotRef = { event_id: string; slot_date: string; start_time: string };
      return (data ?? []).flatMap((row: { student_id: string | null; signup_slots: SlotRef | SlotRef[] | null }) => {
        const slot = Array.isArray(row.signup_slots) ? row.signup_slots[0] : row.signup_slots;
        return row.student_id && slot
          ? [{ eventId: slot.event_id, studentId: row.student_id, slotDate: slot.slot_date, startTime: slot.start_time }]
          : [];
      });
    },
    enabled: activeSignupEventIds.length > 0 && signupStudentIds.length > 0,
  });

  // Fetch solo-parent flags (for co-parent invite nudge)
  const { data: soloParentFlags } = useQuery<Record<string, boolean>>({
    queryKey: ['solo-parent-flags'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_student_solo_parent_flags');
      if (error) return {};
      return (data ?? {}) as Record<string, boolean>;
    },
    enabled: !!students && students.length > 0,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  // Fetch enrollments — fires in parallel with students query (both need parentId only)
  const { data: enrollments, isLoading: enrollmentsLoading, error: enrollmentsError } = useQuery<EnrollmentWithDetails[]>({
    queryKey: ['dashboard-enrollments', parentId, isMultiBrand ? activeBrand : 'all'],
    queryFn: async () => {
      try {
        if (!parentId) throw new Error('No parent');

        const { data: studentParents, error: studentParentsError } = await supabase
          .from('student_parents')
          .select('student_id')
          .eq('parent_id', parentId);

        if (studentParentsError) throw studentParentsError;
        const studentIds = studentParents?.map(sp => sp.student_id) || [];

        const query = supabase
          .from('enrollments')
          .select(`
            id,
            student_id,
            program_id,
            created_at,
            updated_at,
            is_active,
            enrollment_end_date,
            metadata,
            students!inner (
              id,
              first_name,
              last_name,
              date_of_birth
            ),
            programs!inner (
              id,
              name,
              type,
              price,
              billing_type,
              brand_key,
              active,
              start_date,
              end_date,
              schedule_cron,
              duration,
              excluded_dates,
              ${PROGRAM_LOCATION_EMBED},
              created_at,
              updated_at
            ),
            invoices!inner (
              id,
              total_amount,
              amount_paid,
              status,
              due_date,
              payments (
                id,
                amount,
                payment_method,
                payment_date,
                status
              )
            )
          `)
          .in('student_id', studentIds)
          .order('created_at', { ascending: false });

        // One brand at a time, matching the catalog the parent is in — but ONLY while the
        // multi-brand portal is on. Hiding a program a family has not been offered yet is
        // fine; hiding an enrollment they already hold is not, so with the feature off this
        // returns everything exactly as it did before.
        //
        // Safe as a filter on an embedded column only because `programs` is an `!inner`
        // embed — without that, PostgREST keeps the row and nulls the embed instead.
        const { data, error } = await (isMultiBrand
          ? query.eq('programs.brand_key', activeBrand)
          : query);

        if (error) throw error;
        return data as unknown as EnrollmentWithDetails[];
      } catch (err) {
        console.error('Error fetching enrollments:', err);
        throw err;
      }
    },
    enabled: !!parentId,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const billsRecurring = brandProfileOf(activeBrand).billsRecurring;

  // A family that has already saved a card or a verified bank account but never switched Auto
  // Pay on. The method is the hard part and it is done; the switch is one tap. Zelle does not
  // count — it cannot be charged automatically — and neither does a bank still waiting on
  // micro-deposits, because Account refuses to turn Auto Pay on against one. Reads the same
  // three tables, under the same conditions, that Account's own toggle checks.
  const { data: autopayReady = false } = useQuery<boolean>({
    queryKey: ['dashboard-autopay-ready', parentId],
    queryFn: async () => {
      if (!parentId) return false;
      const [prefsRes, cardRes, bankRes] = await Promise.all([
        supabase
          .from('parent_payment_preferences')
          .select('autopay_enabled')
          .eq('parent_id', parentId)
          .maybeSingle(),
        supabase
          .from('card_payment_methods')
          .select('id')
          .eq('parent_id', parentId)
          .eq('is_active', true)
          .limit(1),
        supabase
          .from('bank_account_setup_intents')
          .select('id')
          .eq('parent_id', parentId)
          .eq('is_active', true)
          .eq('status', 'succeeded')
          .limit(1),
      ]);
      // Any failed read means "don't know", and an unknown is not something to nag about.
      if (prefsRes.error || cardRes.error || bankRes.error) return false;
      if (prefsRes.data?.autopay_enabled) return false;
      return (cardRes.data ?? []).length > 0 || (bankRes.data ?? []).length > 0;
    },
    enabled: billsRecurring && !!parentId,
  });

  // The first month the parent can still change — the one the plan editor opens on. Up to the
  // lock day that is next month; after it, next month is frozen and the editor has moved on
  // to the one after, so the nudge moves with it rather than going quiet for two weeks.
  const flexPlanMonth = nextEditableMonth();

  // Flex enrollments that run into the coming month. One ending before it has nothing to plan.
  const flexEnrollmentIds = useMemo(
    () => (enrollments ?? [])
      .filter(e =>
        e.is_active &&
        e.programs?.active &&
        e.programs.billing_type === 'flex_monthly' &&
        (!e.programs.end_date || e.programs.end_date >= flexPlanMonth) &&
        (!e.enrollment_end_date || e.enrollment_end_date >= flexPlanMonth)
      )
      .map(e => e.id)
      .sort(),
    [enrollments, flexPlanMonth]
  );

  // Which of those still have no days for the coming month. A plan with days counts as done
  // whoever filled it in — a seeded draft bills exactly like one the parent typed. An empty
  // plan the parent saved themselves is a decision (a month away), not an omission, so that
  // counts as done too; only a missing plan, or an empty one nobody on the family side has
  // touched, is still waiting on them.
  const { data: flexUnplannedIds = [] } = useQuery<string[]>({
    queryKey: ['dashboard-flex-unplanned', flexPlanMonth, flexEnrollmentIds.join(',')],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('flex_month_plans')
        .select('enrollment_id, dates, status, updated_by')
        .in('enrollment_id', flexEnrollmentIds)
        .eq('plan_month', flexPlanMonth);
      if (error) return [];
      const planned = new Set(
        ((data ?? []) as { enrollment_id: string; dates: string[] | null; status: string; updated_by: string | null }[])
          .filter(plan =>
            (plan.dates ?? []).length > 0 ||
            plan.status !== 'draft' ||
            (plan.updated_by ?? '').startsWith('parent:')
          )
          .map(plan => plan.enrollment_id)
      );
      return flexEnrollmentIds.filter(id => !planned.has(id));
    },
    enabled: flexEnrollmentIds.length > 0,
  });

  // Fetch recommended programs — fires after students load (parallel with enrollments)
  // Uses cached parentId; derives personalization signals from already-fetched students
  const { data: recommendedPrograms } = useQuery<RecommendationResult[]>({
    // Include a students fingerprint so personalization re-runs when the student list changes
    queryKey: ['recommended-programs', parentId, isMultiBrand ? activeBrand : 'all', (students ?? []).map(s => s.id).sort().join(',')],
    queryFn: async () => {
      try {
        if (!parentId) throw new Error('No parent');

        // Derive personalization signals from the already-cached students query
        const studentIds = (students ?? []).map(s => s.id);
        const studentData = (students ?? []).map(s => ({
          id: s.id,
          first_name: s.first_name,
          metadata: s.metadata ?? null,
        }));

        // Aggregate personalization signals across all children
        const studentGrades = new Set<string>();
        const studentSchools = new Set<string>();
        const interestCategories = new Set<string>();
        for (const s of studentData) {
          if (s.metadata?.grade) studentGrades.add(s.metadata.grade);
          if (s.metadata?.school) studentSchools.add(s.metadata.school);
          for (const interest of s.metadata?.interested_programs ?? []) {
            interestCategories.add(interest);
          }
        }

        // Get all active programs with pricing and tags
        const { data: programs, error: programsError } = await supabase
          .from('programs')
          .select(`
            id,
            name,
            type,
            price,
            billing_type,
            active,
            start_date,
            end_date,
            schedule_cron,
            duration,
            excluded_dates,
            ${PROGRAM_LOCATION_EMBED},
            tags,
            created_at,
            updated_at,
            pricing (
              amount,
              is_optional,
              line_item_configs:line_item_config_id (
                id,
                name,
                type,
                description,
                subtype
              )
            )
          `)
          .eq('active', true)
          // Recommendations stay inside the catalog the parent is looking at; suggesting a
          // BHCS class from the Kaleido dashboard would link to a program the Kaleido catalog
          // does not list.
          .eq('brand_key', activeBrand)
          // Never recommend a program that can't be joined — the parent would click through to a
          // detail page with no enroll button.
          .eq('enrollment_status', 'open');
        if (programsError) throw programsError;
        const allPrograms = (programs as unknown as ProgramWithPricing[]) ?? [];

        // Get enrollments to filter out already-enrolled programs and build enrolled-type map
        const { data: currentEnrollments, error: enrollmentsError } = await supabase
          .from('enrollments')
          .select('program_id, student_id')
          .in('student_id', studentIds);
        if (enrollmentsError) throw enrollmentsError;
        const enrolledProgramIds = new Set(currentEnrollments?.map(e => e.program_id));
        const unenrolled = allPrograms.filter(p => !enrolledProgramIds.has(p.id));

        // Build enrolled type → first enrolled program name map (for "enrolled" reason labels)
        const enrolledTypeToProgramName = new Map<string, string>();
        for (const enrollment of currentEnrollments ?? []) {
          const enrolledProgram = allPrograms.find(p => p.id === enrollment.program_id);
          if (enrolledProgram && !enrolledTypeToProgramName.has(enrolledProgram.type)) {
            enrolledTypeToProgramName.set(enrolledProgram.type, enrolledProgram.name);
          }
        }

        // Fetch active enrollment counts per program (for low/high enrollment signals)
        const { data: allEnrollments } = await supabase
          .from('enrollments')
          .select('program_id')
          .eq('is_active', true);
        const enrollmentCountMap = new Map<string, number>();
        for (const e of allEnrollments ?? []) {
          if (e.program_id) {
            enrollmentCountMap.set(e.program_id, (enrollmentCountMap.get(e.program_id) ?? 0) + 1);
          }
        }
        const maxEnrollmentCount = Math.max(0, ...enrollmentCountMap.values());

        // Fetch similar enrollment counts per (school, grade) pair — graceful degradation
        const similarCountMap = new Map<string, number>();
        try {
          for (const school of studentSchools) {
            for (const grade of studentGrades) {
              const { data: similarRows, error: rpcError } = await supabase
                .rpc('get_similar_enrollment_counts', { p_school: school, p_grade: grade });
              if (!rpcError && similarRows) {
                for (const row of similarRows as Array<{ program_id: string; similar_count: number }>) {
                  similarCountMap.set(
                    row.program_id,
                    (similarCountMap.get(row.program_id) ?? 0) + row.similar_count,
                  );
                }
              }
            }
          }
        } catch (rpcErr) {
          console.warn('get_similar_enrollment_counts RPC failed, skipping similar-kids signal:', rpcErr);
        }

        // Score and reason each unenrolled program, then sort descending
        const results = unenrolled
          .map(program =>
            scoreAndReasonProgram(
              program,
              enrollmentCountMap,
              maxEnrollmentCount,
              enrolledTypeToProgramName,
              interestCategories,
              studentGrades,
              studentSchools,
              similarCountMap,
            )
          )
          .sort((a, b) => b.score - a.score);

        // Five, at most: the dashboard's job is to answer today's questions, browsing lives on
        // the Programs page, and a twelfth suggestion below the fold is not a suggestion.
        const scored = results.filter(r => r.score > 0);
        return (scored.length > 0 ? scored : results).slice(0, MAX_RECOMMENDATIONS);
      } catch (err) {
        console.error('Error fetching recommended programs:', err);
        throw err;
      }
    },
    enabled: !!parentId && !!students,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  useEffect(() => {
    let mounted = true;

    async function loadDashboard() {
      try {
        if (mounted) {
          setError(null);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'An error occurred');
        }
      }
    }

    if (!authLoading) {
      loadDashboard();
    }

    return () => {
      mounted = false;
    };
  }, [authLoading]);

  // Determine waiver status from students list metadata
  function hasBhcsWaiver(md: unknown): boolean {
    if (!md || typeof md !== 'object') return false;
    return 'bhcs_waiver' in (md as Record<string, unknown>);
  }
  const studentIdToWaiverSigned = useMemo(() => {
    const map = new Map<string, boolean>();
    (students || []).forEach(s => {
      map.set(s.id, hasBhcsWaiver(s.metadata));
    });
    return map;
  }, [students]);

  // Computed from the enrollments already loaded for the list below (before any early returns),
  // so the nudge and "Needs you" read the same rows and the page makes no second request.
  const campNudge = useUnpaidInvoiceNudge({ enrollments });

  const handleAddChildClick = () => {
    if (settings.allowParentsToAddStudents) {
      // Track the student_create event when modal is opened
      trackEvent({
        eventType: 'student_create',
        user: user,
        userType: 'parent',
        entityType: 'student',
        metadata: {
          action: 'modal_opened',
          source: 'dashboard_page'
        }
      });
      setShowAddChild(true);
    } else {
      setShowContactAdmin(true);
    }
  };

  // Show loading state
  // Recommendations load after main content — don't block the shell on them
  // Who this round is actually for. A round names the grades it covers and the kind of
  // enrollment it assumes; a child has to satisfy both, because the two together are what make
  // them somebody's student to meet about. Rounds created before targeting existed name
  // neither, and those still reach every active child, exactly as they did.
  const signupCandidates: EligibilityCandidate[] = useMemo(() => {
    const typesByStudent = new Map<string, string[]>();
    for (const enrollment of enrollments ?? []) {
      if (!enrollment.is_active || !enrollment.programs?.active) continue;
      const type = enrollment.programs.type;
      if (!type) continue;
      const existing = typesByStudent.get(enrollment.student_id);
      if (existing) existing.push(type);
      else typesByStudent.set(enrollment.student_id, [type]);
    }

    return (students ?? []).map(student => ({
      id: student.id,
      firstName: student.first_name,
      active: student.active,
      grade: student.metadata?.grade ?? null,
      activeProgramTypes: typesByStudent.get(student.id) ?? [],
    }));
  }, [students, enrollments]);

  if (authLoading || parentIdLoading || enrollmentsLoading || studentsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // Show error state
  if (error || enrollmentsError) {
    return (
      <div className="p-6">
        <h1 className="text-xl sm:text-2xl font-bold mb-4">Dashboard</h1>
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-sm sm:text-base text-red-600">Error: {error || enrollmentsError?.message}</p>
        </div>
      </div>
    );
  }

  // Group enrollments by student and separate active/inactive
  const allStudentEnrollments: StudentEnrollments[] = enrollments?.reduce((acc: StudentEnrollments[], enrollment) => {
    const existingStudent = acc.find(item => item.student.id === enrollment.students.id);
    if (existingStudent) {
      existingStudent.enrollments.push(enrollment);
    } else {
      acc.push({
        student: enrollment.students,
        enrollments: [enrollment]
      });
    }
    return acc;
  }, []) || [];

  // Separate active and inactive enrollments
  const activeStudentEnrollments: StudentEnrollments[] = allStudentEnrollments.map(studentGroup => ({
    student: studentGroup.student,
    enrollments: studentGroup.enrollments.filter(enrollment => enrollment.is_active)
  })).filter(studentGroup => studentGroup.enrollments.length > 0);

  // What each child has going on today, for the block at the top of the page. The shape is
  // structural rather than a slice of EnrollmentWithDetails so the section can be handed the
  // same thing from the Students page later without dragging invoices along.
  const todayStudents: TodayStudent[] = activeStudentEnrollments.map(({ student, enrollments }) => ({
    id: student.id,
    name: `${student.first_name} ${student.last_name}`,
    enrollments: enrollments.map(e => ({
      id: e.id,
      programs: e.programs,
      // `metadata.selected_days`, not the `selected_days` column: the column exists and is
      // documented as canonical, and in production it is empty on all 262 per_day
      // enrollments — the enrolment flow writes the metadata mirror. Only `per_day` fills
      // this in at all; Flex reads its month plan instead, and every other billing type
      // ignores the field.
      selectedDays: e.metadata?.selected_days ?? null,
    })),
  }));

  // Precisely the invoices that used to carry a "Pay Now" button on an enrollment row: the
  // latest unpaid one per enrollment, with something actually left to pay, and not already
  // in flight. Promoting them out of the list changes where they are, not which they are.
  const dueInvoices: DueInvoice[] = activeStudentEnrollments.flatMap(({ student, enrollments }) =>
    enrollments.flatMap(enrollment => {
      const { invoice, amountDue, isPayable } = getEnrollmentBilling(enrollment, cartHeldInvoiceIds);
      if (!invoice || !isPayable) return [];
      return [{
        invoiceId: invoice.id,
        studentFirstName: student.first_name,
        programName: enrollment.programs.name,
        amountDue,
        dueDate: invoice.due_date,
      }];
    })
  );

  const inactiveStudentEnrollments: StudentEnrollments[] = allStudentEnrollments.map(studentGroup => ({
    student: studentGroup.student,
    enrollments: studentGroup.enrollments.filter(enrollment => !enrollment.is_active)
  })).filter(studentGroup => studentGroup.enrollments.length > 0);

  const recommendedHeading = label('header.recommendedPrograms');
  const recommendations = recommendedPrograms ?? [];

  // Every active seat, flattened for the timetable. The student travels with the enrollment
  // because a block has to be able to say whose it is and take its colour from that name.
  const weekEnrollments: WeekEnrollment[] = activeStudentEnrollments.flatMap(({ student, enrollments }) =>
    enrollments.map(enrollment => ({
      id: enrollment.id,
      studentId: student.id,
      studentName: `${student.first_name} ${student.last_name}`,
      studentFirstName: student.first_name,
      programs: enrollment.programs,
      // `per_day` carries its bought days here; Flex reads its month plan inside the
      // calendar itself, which is the only component that knows which week it is drawing.
      selectedDays: enrollment.metadata?.selected_days ?? null,
    }))
  );

  // The two nudges that used to hang off each child's row in the enrollment list. The calendar
  // has nowhere to put them, and they are the definition of something waiting on the parent,
  // so they move to "Needs you" rather than disappearing. Scope is unchanged: only children
  // who actually hold an active enrollment are nudged.
  const today = getTodayDateStringInBusinessTZ();

  // Each open round is considered on its own. Two can be open at once — a G4/G5 round and a
  // TK/K one — and a family with a child in each needs both.
  const openRounds = (activeSignupEvents ?? [])
    .filter(round => isRoundOpen(round, today))
    .map(round => {
      const forThisRound = eligibleStudents(signupCandidates, round);
      const eligibleIds = new Set(forThisRound.map(student => student.id));
      const bookings = (signupBookings ?? [])
        .filter(booking => booking.eventId === round.id && eligibleIds.has(booking.studentId));
      const bookedIds = new Set(bookings.map(booking => booking.studentId));
      return {
        round,
        eligible: forThisRound,
        bookings,
        unbooked: forThisRound.filter(student => !bookedIds.has(student.id)),
      };
    })
    .filter(entry => entry.eligible.length > 0);

  // Booking a slot is pinned above the rest: the round is open for a few days and then it is
  // not, whereas a waiver or a co-parent invite is still there next week.
  const signupActions: DueAction[] = openRounds
    .filter(entry => entry.unbooked.length > 0)
    .map(entry => ({
      id: `signup-${entry.round.id}`,
      title: entry.round.title,
      detail: `No time slot yet for ${formatNameList(entry.unbooked.map(student => student.firstName))}`,
      cta: 'Sign up',
      onAction: () => navigate(`/signup/${entry.round.id}`),
    }));

  // Once everyone is booked nothing is owed, but the round stays in "Needs you" as a quiet row
  // naming when each meeting is — a family that has to move a time looks for it here first,
  // not in the quick links. It goes once every booked meeting has started. Inside the round's
  // lock the page can only show the booking, so the button says so.
  const firstNameById = new Map(signupCandidates.map(candidate => [candidate.id, candidate.firstName]));
  const signupBookedActions: DueAction[] = openRounds
    .filter(entry => entry.unbooked.length === 0)
    .flatMap(entry => {
      const upcoming = entry.bookings
        .map(booking => ({ booking, slot: { slot_date: booking.slotDate, start_time: booking.startTime } }))
        .filter(({ slot }) => !hasSlotStarted(slot, now))
        .sort((a, b) => a.booking.slotDate.localeCompare(b.booking.slotDate)
          || a.booking.startTime.localeCompare(b.booking.startTime));
      if (upcoming.length === 0) return [];
      const allLocked = upcoming.every(({ slot }) => isSlotLocked(slot, entry.round.lockHoursBefore, now));
      return [{
        id: `signup-booked-${entry.round.id}`,
        title: entry.round.title,
        detail: upcoming
          .map(({ booking }) => `${firstNameById.get(booking.studentId) ?? 'Booked'} · ${formatWeekdayDateDisplay(booking.slotDate)}, ${formatTimeOfDay(booking.startTime, { hour12: true })}`)
          .join('; '),
        cta: allLocked ? 'View' : 'Change',
        optional: true,
        optionalLabel: 'Booked',
        onAction: () => navigate(`/signup/${entry.round.id}`),
      }];
    });

  // A door, not a debt: the way back to every open round for as long as one is open, booked or
  // not. Points at the index rather than one round, so a second open round is reachable too.
  const showSignupQuickLink = openRounds.length > 0;

  // A Flex month nobody has planned bills nothing and books nothing. It is a suggestion, not a
  // debt — the family may simply not need that month — so it is optional and sinks below the
  // money like the co-parent invite; the deadline in the detail line is what gives it weight.
  // One row per enrollment, since two children can each be missing a plan.
  const unplannedFlexIds = new Set(flexUnplannedIds ?? []);
  const planMonthName = new Date(`${flexPlanMonth}T00:00:00Z`).toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });
  const lockDeadline = new Date(`${addMonths(flexPlanMonth, -1).slice(0, 8)}${String(getFlexConfig().lockDay).padStart(2, '0')}T00:00:00Z`)
    .toLocaleString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  const flexPlanActions: DueAction[] = activeStudentEnrollments.flatMap(({ student, enrollments }) =>
    enrollments
      .filter(enrollment => unplannedFlexIds.has(enrollment.id))
      .map(enrollment => ({
        id: `flex-plan-${enrollment.id}`,
        title: `${student.first_name} · ${enrollment.programs.name}`,
        detail: `No days picked for ${planMonthName} · plan by ${lockDeadline}`,
        cta: 'Pick days',
        optional: true,
        onAction: () => navigate(`/enrollments/${enrollment.id}`),
      }))
  );

  // Nothing is blocked without Auto Pay — invoices can still be paid by hand — so it is optional
  // too.
  const autopayActions: DueAction[] = billsRecurring && autopayReady === true
    ? [{
        id: 'autopay-off',
        title: 'Auto Pay is off',
        detail: 'Your saved payment method can pay invoices on their due date',
        cta: 'Turn on',
        optional: true,
        onAction: () => navigate('/account#payment-settings'),
      }]
    : [];

  const dueActions: DueAction[] = [...signupActions, ...signupBookedActions, ...flexPlanActions, ...activeStudentEnrollments.flatMap(({ student }) => {
    const fullName = `${student.first_name} ${student.last_name}`;
    const items: DueAction[] = [];
    if (!studentIdToWaiverSigned.get(student.id)) {
      items.push({
        id: `waiver-${student.id}`,
        title: fullName,
        detail: 'Waiver not signed',
        cta: 'Sign waiver',
        onAction: () => navigate('/student-waiver'),
      });
    }
    if (soloParentFlags?.[student.id]) {
      items.push({
        id: `co-parent-${student.id}`,
        title: fullName,
        detail: 'No co-parent has access',
        cta: 'Invite',
        // Nothing is blocked by not doing this, unlike the waiver above it and the conference
        // slot above that, so it is marked optional and sinks below the invoices.
        optional: true,
        onAction: () => navigate('/students', { state: { openInviteForStudentId: student.id } }),
      });
    }
    return items;
  }), ...autopayActions];

  /* Where the page can take you next, in the order a family is likely to want them.
     Enrollment history is offered only when there is any — the link used to sit on the week
     grid under the same condition, and a parent in their first term should not be sent to an
     empty page. */
  const quickLinks: QuickLink[] = [
    ...(showSignupQuickLink
      ? [{
          labelKey: 'nav.meetingSignup' as const,
          to: '/signup',
          detail: 'Book, change or cancel a meeting time',
          icon: CalendarClock,
          tone: 'sky' as const,
        }]
      : []),
    ...(inactiveStudentEnrollments.length > 0
      ? [{
          labelKey: 'header.enrollmentHistory' as const,
          to: '/enrollment-history',
          detail: 'Past terms and classes that have ended',
          icon: History,
          tone: 'amber' as const,
        }]
      : []),
    {
      labelKey: 'nav.assistant',
      to: '/assistant',
      detail: 'Ask about invoices, schedules and enrollment',
      icon: Sparkles,
      tone: 'violet',
    },
    {
      labelKey: 'nav.bhcsCash',
      to: '/wallet',
      detail: 'Balance, add funds and activity',
      icon: WalletIcon,
      tone: 'emerald',
    },
    /*
      Autopay shortcut, for the brands that bill on a recurring schedule.

      Kaleido sells fixed-term series, one-off workshops and free events — nothing recurs, so
      "pay invoices automatically when they are due" is a shortcut to a setting that would
      never fire. Hidden from the quick actions, NOT from Account: the setting is per family,
      not per brand, and a family in both brands still needs to reach it from the one place it
      lives.
    */
    ...(brandProfileOf(activeBrand).billsRecurring
      ? [{
          labelKey: 'nav.autoPay' as const,
          to: '/account#payment-settings',
          detail: 'Pay invoices automatically when they are due',
          icon: Repeat,
          tone: 'sky' as const,
        }]
      : []),
  ];

  /** Whether the desktop layout has a right-hand column to fill. */
  const asideOpen = dueInvoices.length > 0 || dueActions.length > 0 || recommendations.length > 0;

  // Check if there are any active students
  const hasActiveStudents = students?.some(student => student && student.active) || false;

  // getStatusColor is imported from shared types/status

  // If user has no children, show AddChildForm modal
  if (!students || students.length === 0) {
    const handleAddChildClose = async () => {
      setShowAddChild(false);
      await queryClient.invalidateQueries();
    };
    return (
      <>
        <div className="p-4">
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <p className="text-sm sm:text-base text-gray-600 mb-4">To get started, please add your child.</p>
            <button
              className="bg-blue-600 text-white px-6 py-2 rounded-md font-medium hover:bg-blue-700 transition-colors"
              onClick={handleAddChildClick}
            >
              Add Child
            </button>
          </div>
        </div>
        {showAddChild && <AddChildForm onClose={handleAddChildClose} />}
        
        {showContactAdmin && (
          <div className="fixed inset-0 z-[60] flex flex-col justify-end sm:items-center sm:justify-center">
            <div className="absolute inset-0 bg-gray-500/75" onClick={() => setShowContactAdmin(false)} aria-hidden="true" />
            <div className="relative w-full bg-white rounded-t-2xl shadow-xl sm:rounded-lg sm:max-w-md sm:my-8 flex flex-col max-h-[90vh]">
              <div className="flex justify-center pt-3 pb-1 sm:hidden" aria-hidden="true">
                <div className="w-10 h-1 rounded-full bg-gray-300" />
              </div>
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
                <h3 className="text-base font-semibold text-gray-900">Contact Administrator</h3>
                <button onClick={() => setShowContactAdmin(false)} className="text-gray-500 hover:text-gray-700 p-1.5 rounded" aria-label="Close">
                  <span aria-hidden className="text-xl leading-none">×</span>
                </button>
              </div>
              <div className="overflow-y-auto p-5" style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom, 0px))" }}>
                <div className="flex items-start gap-3 mb-5">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-gray-600">
                    To add a new student to BHCS, please reach out to our billing team at{' '}
                    <a href={`mailto:${settings.supportEmail}`} className="text-blue-600 hover:text-blue-800">
                      {settings.supportEmail}
                    </a>
                  </p>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowContactAdmin(false)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
    {campNudge.showBlocking && (
      <CampPaymentNudge
        invoices={campNudge.invoices}
        onDismiss={campNudge.dismiss}
      />
    )}
    <div className={isMobile ? 'p-2' : 'p-4'}>
      {campNudge.showBanner && (
        <CampPaymentNudge invoices={campNudge.invoices} variant="banner" onDismiss={campNudge.dismiss} />
      )}
      {/* Two independent columns, one DOM, one source of order.
          Not a grid: a grid would put Today and "Needs you" in the same *row*, so the week
          could not start until the taller of them ended — with a long recommendation list that
          left a great deal of blank page under Today. Flex columns let each side flow at its
          own height.
          `contents` is what keeps the phone order right without a second copy of the markup:
          below lg both wrappers vanish from the box tree, all four sections become children of
          the outer column, and order-1..4 interleaves them into Today → Needs you → This week
          → Recommended. At lg the wrappers become real columns and the ordering drops away. */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <div className="contents lg:flex lg:min-w-0 lg:flex-1 lg:flex-col lg:gap-4">
          <TodaySection students={todayStudents} className="order-1 lg:order-none" />

          {/* A parent whose children are still awaiting approval has no schedule to draw, so the
              waiting notice keeps the slot the calendar would occupy. */}
          {!hasActiveStudents ? (
            <section className="order-3 rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 lg:order-none">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg shadow-lg p-6">
                <div className="text-center">
                  <div className="flex justify-center mb-4">
                    <span className="text-3xl">✨</span>
                  </div>
                  <h3 className="text-lg font-semibold text-blue-800 mb-2">Almost There!</h3>
                  <p className="text-blue-700 mb-4">
                    Your little ones are in the queue for approval. We're excited to have them join our BHCS family!
                  </p>
                  <div className="bg-blue-100 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="text-sm text-blue-700 space-y-2">
                      <p className="flex items-center gap-2">
                        <span className="text-lg">👀</span>
                        <span>Our admin team is currently reviewing your student’s information and the programs you’re interested in.</span>
                      </p>
                      <p className="flex items-center gap-2">
                        <span className="text-lg">🎯</span>
                        <span>Your student will appear as active in your account once the review is complete.</span>
                      </p>
                      <p className="flex items-center gap-2">
                        <span className="text-lg">📧</span>
                        <span>You will receive a confirmation email within 1–2 business days, at which point your student will be ready to enroll in our programs.</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          ) : (
            <WeekCalendar enrollments={weekEnrollments} className="order-3 lg:order-none" />
          )}

          {/* Last on both layouts: it is the block you read when the three above it have
              already answered what you came for. */}
          <QuickLinksSection links={quickLinks} className="order-5 lg:order-none" />
        </div>

        {asideOpen && (
          <div className="contents lg:flex lg:w-[360px] lg:flex-none lg:flex-col lg:gap-4">
            <NeedsYouSection invoices={dueInvoices} actions={dueActions} className="order-2 lg:order-none" />
            {recommendations.length > 0 && (
              <section
                className="order-4 rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 lg:order-none"
                aria-label={recommendedHeading.en}
              >
                <h2 className={`${TYPE_SECTION_WIDE} mb-3`}>
                  {recommendedHeading.zh && (
                    <span className="mr-1.5 font-normal normal-case tracking-normal">{recommendedHeading.zh}</span>
                  )}
                  {recommendedHeading.en}
                </h2>
                {/* One card per row rather than a 2–3 column grid. The cards themselves are
                    unchanged — same reason chip that expands on tap, same pricing, same CTA —
                    only the shape of the container they sit in follows the new layout. */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1">
                  {recommendations.map(result => (
                    <ProgramCard
                      key={result.program.id}
                      program={result.program}
                      reasonLabel={result.reason ?? undefined}
                      reasonType={result.reasonType ?? undefined}
                      reasonExpanded={activeReasonId === result.program.id}
                      onReasonToggle={() =>
                        setActiveReasonId(id => (id === result.program.id ? null : result.program.id))
                      }
                    />
                  ))}
                </div>
                <Link
                  to="/programs"
                  className="mt-3 inline-block text-xs font-medium text-[color:var(--brand-accent)] hover:text-[color:var(--brand-accent-ink)] hover:underline"
                >
                  {t('action.allPrograms')} →
                </Link>
              </section>
            )}
          </div>
        )}
      </div>

    </div>
    </>
  );
}
